import { useEffect, useState } from "react";
import { Link } from "react-router";
import { BellRing, Loader2 } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

interface ReminderSettingsResponse {
  data: {
    defaults: {
      enabled: boolean;
      offsetsMinutes: number[];
      channels: Array<"email" | "sms">;
    };
    services: Array<{
      id: string;
      name: string;
      reminderSettings: {
        enabled: boolean;
        offsetsMinutes: number[];
        channels: Array<"email" | "sms">;
      };
      updatedAt: string;
    }>;
  };
}

interface BookingReminderRow {
  id: string;
  reminderType: string;
  channel: "email" | "sms";
  scheduledAt: string;
  sentAt: string | null;
  status: "pending" | "sent" | "failed" | "cancelled";
  createdAt: string;
}

function formatOffsets(offsets: number[]) {
  return offsets.map((minutes) => {
    if (minutes % 1440 === 0) return `${minutes / 1440}d`;
    if (minutes % 60 === 0) return `${minutes / 60}h`;
    return `${minutes}m`;
  }).join(", ");
}

export default function ServiceRemindersPage() {
  const [settings, setSettings] = useState<ReminderSettingsResponse["data"] | null>(null);
  const [bookingId, setBookingId] = useState("");
  const [reminders, setReminders] = useState<BookingReminderRow[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [error, setError] = useState("");
  const [reminderError, setReminderError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      setLoadingSettings(true);
      setError("");

      try {
        const response = await apiFetch<ReminderSettingsResponse>("/crm/bookings/reminders/settings");
        if (!cancelled) {
          setSettings(response.data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof ApiError
              ? loadError.message
              : loadError instanceof Error
                ? loadError.message
                : "Failed to load reminder settings.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingSettings(false);
        }
      }
    }

    void loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadBookingReminders() {
    const trimmedBookingId = bookingId.trim();
    if (!trimmedBookingId) {
      setReminderError("Enter a booking ID to inspect reminder rows.");
      return;
    }

    setLoadingReminders(true);
    setReminderError("");
    setReminders([]);

    try {
      const response = await apiFetch<{ data: BookingReminderRow[] }>(
        `/crm/bookings/${encodeURIComponent(trimmedBookingId)}/reminders`,
      );
      setReminders(response.data ?? []);
    } catch (loadError) {
      setReminderError(
        loadError instanceof ApiError
          ? loadError.message
          : loadError instanceof Error
            ? loadError.message
            : "Failed to load booking reminders.",
      );
    } finally {
      setLoadingReminders(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.45)]">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-[var(--color-primary)]/10 p-3 text-[var(--color-primary)]">
            <BellRing className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Mounted reminder pipeline</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Booking reminders</h1>
          </div>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)]">
          Reminder timing and channels are stored on the live service catalog. Booking create and reschedule use those settings;
          booking cancel marks unsent rows as cancelled, and the CRM cron queues due email/SMS reminders.
        </p>
        <Link
          to="/services/catalog"
          className="mt-5 inline-flex rounded-2xl border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
        >
          Edit reminder settings in Service Catalog
        </Link>
      </div>

      {error && (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Effective service settings</h2>
        {loadingSettings ? (
          <p className="mt-4 text-sm text-[var(--color-muted-foreground)]">Loading reminder settings...</p>
        ) : settings ? (
          <div className="mt-4 overflow-hidden rounded-2xl border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                <tr>
                  <th className="px-5 py-4 font-medium">Service</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Timing</th>
                  <th className="px-5 py-4 font-medium">Channels</th>
                  <th className="px-5 py-4 font-medium">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {settings.services.map((service) => (
                  <tr key={service.id}>
                    <td className="px-5 py-4 text-sm font-medium text-[var(--color-foreground)]">{service.name}</td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">
                      {service.reminderSettings.enabled ? "Enabled" : "Disabled"}
                    </td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">
                      {formatOffsets(service.reminderSettings.offsetsMinutes)}
                    </td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">
                      {service.reminderSettings.channels.join(", ")}
                    </td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">
                      {formatDateTime(service.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Inspect a booking</h2>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={bookingId}
            onChange={(event) => setBookingId(event.target.value)}
            placeholder="Booking UUID"
            className="h-11 flex-1 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
          />
          <button
            type="button"
            onClick={() => void loadBookingReminders()}
            disabled={loadingReminders}
            className="inline-flex h-11 items-center justify-center rounded-2xl bg-[var(--color-primary)] px-4 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {loadingReminders ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading
              </>
            ) : "Load reminders"}
          </button>
        </div>
        {reminderError && (
          <p className="mt-3 text-sm text-[var(--color-error)]">{reminderError}</p>
        )}
        {reminders.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-[var(--color-border)]">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                <tr>
                  <th className="px-5 py-4 font-medium">Window</th>
                  <th className="px-5 py-4 font-medium">Channel</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Scheduled</th>
                  <th className="px-5 py-4 font-medium">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {reminders.map((reminder) => (
                  <tr key={reminder.id}>
                    <td className="px-5 py-4 text-sm font-medium text-[var(--color-foreground)]">{reminder.reminderType}</td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">{reminder.channel}</td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">{reminder.status}</td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">{formatDateTime(reminder.scheduledAt)}</td>
                    <td className="px-5 py-4 text-sm text-[var(--color-muted-foreground)]">
                      {reminder.sentAt ? formatDateTime(reminder.sentAt) : "Not sent"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
